import cx from "classnames";
import React, { Suspense, useRef, useState } from "react";
import { connect } from "react-redux";
import TurndownService from "turndown";
import { useAppService } from "../../../../hooks/use-app-service";
import { redirectToAuthUrl } from "../../../../_config/helper.config";
import { createPostDetailPageRouteFromSlug } from "../../../../_pages/post-detail.page";
import { PostService } from "../../../../_service";
import AnalyticsService, {
  EventType,
} from "../../../../_service/analytics-service";
import { MentionService } from "../../../../_service/mention.service";
import { shortenText } from "../../../../_utils/extensions/typescript-utils";
import checkForProfanity from "../../../../_utils/restricted-words";
import Validator from "../../../../_utils/validator";
import { ActionModal } from "../../../action-modal.component";
import { CloseButton } from "../../../activity/close-button.component";
import I18 from "../../../atoms/i18";
import Tooltip from "../../../atoms/tooltip";
import { Avatar } from "../../../avatar.component";
import ErrorBoundary from "../../../error-boundry.component";
import { FileInput } from "../../../form-controls/file-input";
import { SendButton } from "../../../molecule/send-button";
import TenorGifPicker from "../../../tenor-gif-picker";
import UIcon from "../../../uicon-component";
import PostAudioRecorder from "../../create/component/post-audio-recorder.component";
const RichTextEditor = React.lazy(
  () => import("../../../form-controls/rich-text-editor.component")
);

const tds = new TurndownService();

/**
 * Component to create comment
 * @param {*} param0
 * @returns
 */
function CreateCommentComponent({
  post,
  user,
  updatePost = (e) => {},
  isSubComment = false,
  commentId,
  community,
}) {
  const { analyticsService } = useAppService();
  const [description, setDescription] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  // media related
  const [images, setImages] = useState([]);
  const [document, setDocument] = useState(null);

  // Recorder related
  const [isRecorderVisible, setIsRecorderVisible] = useState(false);

  // doc upload for sub comment
  const [subDocument, setSubDocument] = useState([]);
  const [videos, setVideos] = useState([]);
  const textAreaElement = useRef();
  const [isGifPickerVisible, setIsGifPickerVisible] = useState(false);

  const [profanityWords, setProfanityWords] = useState([]);

  // Synchronous re-entrancy lock. `isPosting` is React state and is also set
  // only after the profanity check's `await` below, so it can't block a rapid
  // second click (the classic duplicate-comment cause). A ref flips instantly
  // and gates every submit until the current one settles.
  const isSubmittingRef = useRef(false);

  // if no user, dont show the comment box
  if (!user) {
    return (
      <section className="border-b border-l border-r border-border bg-background p-4">
        <span
          className="cursor-pointer text-primary"
          onClick={(e) => {
            redirectToAuthUrl(community);
          }}>
          Sign in
        </span>{" "}
        <span>to add a comment,</span>
      </section>
    );
  }

  async function onSubmit() {
    // create comment

    // Block duplicate submits from a double-click / impatient re-click while a
    // comment is still in flight. Must be checked & set BEFORE the first await.
    if (isSubmittingRef.current) {
      return;
    }

    if (!description) {
      return window.alert("Comment text is required!");
    }

    isSubmittingRef.current = true;
    setIsPosting(true);

    try {
      const restrictedWords = await checkForProfanity(description);
      if (restrictedWords.length > 0) {
        setProfanityWords(restrictedWords);
        return;
      }

      if (!isSubComment) {
        const embedlessComment = await description.replace(
          /(?:<figure class="media"><oembed url=")([^"^<^>^\[^\]]+)(?:"><\/oembed><\/figure>)/g,
          " $1 "
        );
        let finalComment = await tds.turndown(embedlessComment);

        const hasAttachment = images.length > 0 || Boolean(document);

        if (!hasAttachment) {
          // ---- Optimistic text comment ----
          // Show the comment in the list immediately, then save it in the
          // background and swap in the server's copy (or roll back on failure).
          // The user sees their comment instantly instead of waiting on the
          // round-trip (2026-09-01: the comment endpoint was also 500ing ~48% of
          // the time, leaving the UI un-updated until refresh).
          const submittedText = description;
          const baseComments = Array.isArray(post.comments)
            ? post.comments
            : [];
          const optimisticComment = {
            id: "temp-" + Date.now(),
            description: finalComment,
            createdBy: {
              id: user.id,
              name: user.name,
              userId: user.userId,
              picture: user.picture,
              isVerifiedByPensil: user.isVerifiedByPensil,
              role: user.role,
            },
            createdAt: new Date().toISOString(),
            reactions: { total: 0, details: [] },
            replies: [],
            isByMe: true,
            images: [],
            document: null,
          };

          // clear the input + render the comment right away
          setDescription("");
          if (textAreaElement.current) {
            window.setTimeout(() => {
              textAreaElement.current.style.height = "auto";
            }, 0);
          }
          updatePost({
            ...post,
            comments: [optimisticComment, ...baseComments],
            latestComment: optimisticComment,
            commentCount: (post.commentCount || 0) + 1,
          });

          AnalyticsService.logEvent(
            EventType.Post.comment.name,
            EventType.Post.comment.create
          );
          try {
            analyticsService.track("post-comment", {
              comment: submittedText,
              groupName: post?.group?.name,
              sectionName: post?.tab?.name,
              attachment: false,
              postUrl:
                window.location.origin +
                createPostDetailPageRouteFromSlug(post),
            });
          } catch (analyticsError) {
            console.error("post-comment analytics failed", analyticsError);
          }

          // background save + reconcile
          try {
            const { comment: saved, commentCount } =
              await PostService.addComment(user, post.id, {
                description: finalComment,
              });
            updatePost({
              ...post,
              comments: [saved || optimisticComment, ...baseComments],
              latestComment: saved || optimisticComment,
              commentCount:
                commentCount !== undefined
                  ? commentCount
                  : (post.commentCount || 0) + 1,
            });
          } catch (err) {
            // roll back the optimistic comment and restore the text to retry
            console.error("comment submit failed", err);
            updatePost({
              ...post,
              comments: baseComments,
              latestComment: baseComments[0] || post.latestComment || null,
              commentCount: post.commentCount || 0,
            });
            setDescription(submittedText);
            window.alert("Couldn't post your comment. Please try again.");
          }
          return;
        }

        // ---- Attachment comment ----
        // Needs the server-assigned commentId to upload against, so this path
        // waits for the round-trip (uncommon).
        let { commentId } = await PostService.addComment(user, post.id, {
          description: finalComment,
        });
        AnalyticsService.logEvent(
          EventType.Post.comment.name,
          EventType.Post.comment.create
        );

        let uploadedPost = null;
        if (images.length > 0) {
          uploadedPost = (
            await PostService.uploadImagesToComment(
              user,
              post.id,
              commentId,
              images
            )
          ).post;
        }
        if (document) {
          uploadedPost = (
            await PostService.uploadDocumentToComment(
              user,
              post.id,
              commentId,
              document
            )
          ).post;
        }

        setImages([]);
        setDocument(null);
        setDescription("");
        if (textAreaElement.current) {
          window.setTimeout(() => {
            textAreaElement.current.style.height = "auto";
          }, 0);
        }
        if (uploadedPost) {
          updatePost(uploadedPost);
        }

        try {
          analyticsService.track("post-comment", {
            comment: description,
            groupName: post?.group?.name,
            sectionName: post?.tab?.name,
            attachment: true,
            postUrl:
              window.location.origin + createPostDetailPageRouteFromSlug(post),
          });
        } catch (analyticsError) {
          console.error("post-comment analytics failed", analyticsError);
        }
      } else {
        // create Sub Comment
        const embedlessSubComment = await description.replace(
          /(?:<figure class="media"><oembed url=")([^"^<^>^\[^\]]+)(?:"><\/oembed><\/figure>)/g,
          " $1 "
        );
        let finalSubComment = await tds.turndown(embedlessSubComment);

        let { commentReply: commentReply } = await PostService.addCommentReply(
          user,
          post.id,
          commentId,
          { description: finalSubComment }
        );
        AnalyticsService.logEvent(
          EventType.Post.comment.reply.name,
          EventType.Post.comment.reply.create
        );

        const hadReplyAttachment =
          Boolean(document) || images.length > 0;

        // if document
        if (subDocument.length > 0) {
          commentReply = (
            await PostService.uploadDocumentToCommentReply(
              user,
              post.id,
              commentId,
              commentReply.id,
              subDocument
            )
          ).commentReply;
        }
        // if image
        if (images.length > 0) {
          commentReply = (
            await PostService.uploadDocumentToCommentReply(
              user,
              post.id,
              commentId,
              commentReply.id,
              images
            )
          ).commentReply;
        }
        // if video
        if (videos.length > 0) {
          commentReply = (
            await PostService.uploadDocumentToCommentReply(
              user,
              post.id,
              commentId,
              commentReply.id,
              videos
            )
          ).commentReply;
        }

        setDescription("");
        setSubDocument([]);
        setImages([]);
        setVideos([]);
        if (textAreaElement.current) {
          window.setTimeout(() => {
            textAreaElement.current.style.height = "auto";
            // textAreaElement.current.style.height = e.target.scrollHeight + "px";
          }, 0);
        }
        updatePost(commentReply, commentId);

        // mixpanel event — LAST + guarded, same rationale as the main branch.
        try {
          analyticsService.track("post-reply", {
            comment: description,
            groupName: post?.group?.name,
            sectionName: post?.tab?.name,
            attachment: hadReplyAttachment,
            postUrl:
              window.location.origin + createPostDetailPageRouteFromSlug(post),
          });
        } catch (analyticsError) {
          console.error("post-reply analytics failed", analyticsError);
        }
      }
    } catch (error) {
      // Log so a failure in this path is visible instead of silently leaving
      // the comment in the box; the user can retry.
      console.error("comment submit failed", error);
    } finally {
      // Always release the lock and clear the loading flag, success or error.
      isSubmittingRef.current = false;
      setIsPosting(false);
    }
  }
  return (
    <ErrorBoundary>
      <div className="flex py-2">
        <div>
          <Avatar
            user={user}
            hideNameOnMobile
            noName
            size={36}
            className="cursor-pointer"
          />
        </div>
        <div
          className={cx(
            "flex w-full flex-col items-start rounded-xl border border-border px-4 py-2",
            {
              "": !isSubComment,
              "bg-card": isSubComment,
            }
          )}>
          {/* media preview */}
          {images.length > 0 ? (
            <div className="relative mb-2">
              <img
                className="h-40 w-full rounded-xl object-cover"
                src={URL.createObjectURL(images[0])}
                alt="CommentImage"
              />
              <CloseButton
                className="absolute -right-2 top-0 bg-alert/80 text-alert-foreground/95 hover:bg-alert hover:text-alert-foreground"
                size="sm"
                onClick={(e) => {
                  setImages([]);
                }}
              />
            </div>
          ) : (
            <></>
          )}
          {videos.length > 0 ? (
            <div className="relative">
              <div className="mt-2 py-1">
                <video
                  className="m-0 w-full"
                  controls
                  controlsList="nodownload">
                  <source src={URL.createObjectURL(videos[0])} />
                </video>
              </div>
              <CloseButton
                className="absolute -right-2 top-0 bg-alert/80 text-alert-foreground/95 hover:bg-alert hover:text-alert-foreground"
                size="sm"
                onClick={(e) => {
                  setVideos([]);
                }}
              />
            </div>
          ) : (
            <></>
          )}
          {document &&
          ["mp4", "mov", "webm", "ogg"].includes(
            document.name.split(".").pop()
          ) ? (
            <div className="relative">
              <div className="mt-2 py-1">
                <video className="m-0" controls controlsList="nodownload">
                  <source src={URL.createObjectURL(document)} />
                </video>
              </div>
              <CloseButton
                className="absolute -right-2 top-0 bg-alert/80 text-alert-foreground/95 hover:bg-alert hover:text-alert-foreground"
                size="sm"
                onClick={(e) => {
                  setDocument(null);
                }}
              />
            </div>
          ) : (
            <SelectedDocumentPreview
              document={document}
              isSubComment={isSubComment}
              subDocument={subDocument}
              setDocument={setDocument}
              setSubDocument={setSubDocument}
            />
          )}
          <PostAudioRecorder
            isRecorderVisible={isRecorderVisible}
            setIsRecorderVisible={setIsRecorderVisible}
            setRecording={(recording) => {
              if (isSubComment) {
                setVideos([]);
                setImages([]);
                setSubDocument([]);
                setSubDocument([recording]);
              } else {
                setImages([]);
                setDocument(recording);
              }
            }}
          />
          <Suspense fallback={<></>}>
            <RichTextEditor
              className="noBorder minHeightDefault w-full overflow-y-auto text-xs text-secondary-foreground"
              text={description}
              setText={setDescription}
              placeholder={isSubComment ? "Write Reply..." : "Write Comment..."}
              getMentionsFeed={async (query) => {
                const users = (
                  await MentionService.searchInCommunity(
                    user,
                    community.id,
                    query
                  )
                ).users.map((user, index) => {
                  const useruid = user.id;
                  user.id = "@" + user.userId;
                  user.userId = useruid;
                  return user;
                });
                return users;
              }}
            />
          </Suspense>

          {/* controls */}
          <div className="flex w-full items-center justify-between">
            {/* Toolbar buttons */}
            <div className="Toolbar flex items-center gap-x-5">
              <Tooltip label="Picture">
                <FileInput
                  accept="image/*"
                  maxFileSize={10}
                  disabled={images.length > 0}
                  onChange={
                    images.length > 0
                      ? () => {}
                      : (img) => {
                          setImages([img]);
                          setDocument(null);
                          setSubDocument([]);
                          setVideos([]);
                        }
                  }>
                  <div
                    className={cx(
                      "cursor-pointe text-secondary-foreground/85 hover:scale-110",
                      {
                        "cursor-not-allowed text-secondary-foreground/70":
                          images.length > 0,
                      }
                    )}>
                    <UIcon size="md" icon="picture" />
                  </div>
                </FileInput>
              </Tooltip>

              {/* VIDEO */}
              <Tooltip label="Video" className={!setVideos && "hidden"}>
                <FileInput
                  accept="video/*"
                  maxFileSize={100}
                  disabled={videos.length > 0}
                  onChange={(video) => {
                    if (video) {
                      setVideos([video]);
                      setImages([]);
                      setSubDocument([]);
                      setDocument(null);
                    }
                  }}>
                  <div
                    className={cx(
                      "cursor-pointer text-secondary-foreground/85 hover:scale-110",
                      {
                        "cursor-not-allowed text-secondary-foreground/70":
                          videos.length > 0,
                      }
                    )}>
                    <UIcon size="md" icon="play-alt" />
                  </div>
                </FileInput>
              </Tooltip>

              {/* ATTACHMENT */}
              <Tooltip label="Attachment">
                <FileInput
                  accept="file/*"
                  maxFileSize={100}
                  disabled={document || subDocument.length > 0}
                  onChange={(doc) => {
                    if (isSubComment) {
                      setVideos([]);
                      setImages([]);
                      setSubDocument([]);
                      setSubDocument([doc]);
                    } else {
                      setDocument(null);
                      setImages([]);
                      setDocument(doc);
                    }
                  }}>
                  <div
                    className={cx(
                      "cursor-pointer text-secondary-foreground/85 hover:scale-110",
                      {
                        "cursor-not-allowed text-secondary-foreground/70":
                          document || subDocument.length > 0,
                      }
                    )}>
                    <UIcon size="md" icon="file-add" />
                  </div>
                </FileInput>
              </Tooltip>
              {/* Audio */}
              <Tooltip label="Record Audio">
                <button
                  disabled={document || subDocument.length > 0}
                  className={cx(
                    "cursor-pointer text-secondary-foreground/85 hover:scale-110",
                    {
                      "cursor-not-allowed text-secondary-foreground/70":
                        document || subDocument.length > 0,
                    }
                  )}
                  onClick={() => {
                    setIsRecorderVisible(!isRecorderVisible);
                  }}>
                  <UIcon size="md" icon="microphone" />
                </button>
              </Tooltip>
              {/* GIF */}
              <Tooltip
                label="Gif"
                className={!setIsGifPickerVisible && "hidden"}>
                <button
                  className="p-.05 mb-1 hover:scale-110"
                  onClick={() => {
                    setIsGifPickerVisible(!isGifPickerVisible);
                  }}>
                  <span className="text-xxs rounded-sm border border-secondary-foreground px-0.5">
                    <I18>Gif</I18>
                  </span>
                </button>
              </Tooltip>
              <TenorGifPicker
                active={isGifPickerVisible}
                setActive={setIsGifPickerVisible}
                initialSearchQuery=""
                onGifSelect={async (gif) => {
                  setImages([gif.blobData]);
                  setDocument(null);
                  setSubDocument([]);
                  setVideos([]);
                  setIsGifPickerVisible(false);
                }}
              />
            </div>
            <SendButton
              onClick={onSubmit}
              disabled={!description || isPosting}
              isLoading={isPosting}
            />
          </div>
        </div>
        {/* media preview */}
        {images.length > 0 ? (
          <div className="preview-image mb-2">
            <img
              className="h-40 w-full"
              src={URL.createObjectURL(images[0])}
              alt="CommentImage"
            />
            <span
              className="remove"
              onClick={(e) => {
                setImages([]);
              }}>
              &times;
            </span>
          </div>
        ) : (
          <></>
        )}
        {videos.length > 0 ? (
          <div className="preview-video">
            <div className="mt-2 py-1">
              <video className="m-0 w-full" controls controlsList="nodownload">
                <source src={URL.createObjectURL(videos[0])} />
              </video>
            </div>
            <span
              className="remove"
              onClick={(e) => {
                setVideos([]);
              }}>
              &times;
            </span>
          </div>
        ) : (
          <></>
        )}
        {document &&
        ["mp4", "mov", "webm", "ogg"].includes(
          document.name.split(".").pop()
        ) ? (
          <div className="preview-video">
            <div className="mt-2 py-1">
              <video className="m-0" controls controlsList="nodownload">
                <source src={URL.createObjectURL(document)} />
              </video>
            </div>
            <span
              className="remove"
              onClick={(e) => {
                setDocument(null);
              }}>
              &times;
            </span>
          </div>
        ) : (
          <SelectedDocumentPreview
            document={document}
            isSubComment={isSubComment}
            subDocument={subDocument}
            setDocument={setDocument}
            setSubDocument={setSubDocument}
          />
        )}
        <PostAudioRecorder
          isRecorderVisible={isRecorderVisible}
          setIsRecorderVisible={setIsRecorderVisible}
          setRecording={(recording) => {
            if (isSubComment) {
              setVideos([]);
              setImages([]);
              setSubDocument([]);
              setSubDocument([recording]);
            } else {
              setImages([]);
              setDocument(recording);
            }
          }}
        />
        <ActionModal
          active={profanityWords.length > 0}
          header="Message"
          buttons={[
            {
              label: "OK",
              type: "primary",
              onClick: () => {
                setProfanityWords([]);
              },
            },
          ]}
          children={
            <div className="flex flex-col px-4 pb-4">
              <p className="font-bold">
                The following links are restricted and cannot be used in the
                comment
              </p>
              <ul className="ml-4 list-decimal">
                {profanityWords.map((word) => (
                  <li key={word} className="text-secondary-foreground/80 text-sm">
                    {word}
                  </li>
                ))}
              </ul>
            </div>
          }
          setActive={(e) => {
            setProfanityWords([]);
          }}
          onSubmit={() => {
            setProfanityWords([]);
          }}
        />
      </div>
    </ErrorBoundary>
  );
}

/**
 * Preview document component
 * @param {object} props
 *
 */
function SelectedDocumentPreview({
  document = null,
  subDocument = [],
  setDocument = (e) => {},
  setSubDocument = (e) => {},
  isSubComment,
}) {
  let file = document;
  if (!Validator.hasValue(file) && Validator.hasValue(subDocument)) {
    file = subDocument[0];
  }
  if (!file) {
    return <></>;
  }

  if (["mp3"].includes(file.name.split(".").pop())) {
    return (
      <>
        <div className="SelectedDocumentPreview w-full">
          <div className="relative mt-2 w-full py-3">
            <audio
              src={URL.createObjectURL(file)}
              controls
              className={cx("w-full py-2")}
            />
            <CloseButton
              className="absolute -right-2 top-1 bg-alert/80 text-alert-foreground/95 hover:bg-alert hover:text-alert-foreground"
              size="sm"
              onClick={(e) => {
                if (isSubComment) {
                  setSubDocument([]);
                } else {
                  // remove the videos
                  setDocument(null);
                }
              }}
            />
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="SelectedDocumentPreview relative my-3">
        <div className="flex items-center space-x-2 rounded border-2 border-border px-4 py-2 font-semibold text-secondary-foreground/70">
          <UIcon icon="document" className="h-9 text-3xl" />
          <div className="flex w-full flex-grow items-center justify-between space-x-4">
            <div className="w-full items-center text-ellipsis">
              <div className="line-clamp-2 w-full text-clip font-normal text-secondary-foreground/70">
                {shortenText(file.name, 60, false)}
              </div>
              <div className="text-xs font-semibold uppercase text-secondary-foreground">
                {file.name.split(".").pop()}
              </div>
            </div>
            {/* <span className="flex-none">
              <UIcon icon="download" className="text-base" />
            </span> */}
          </div>
        </div>
        <CloseButton
          className="absolute -right-2 -top-2 bg-alert/80 text-alert-foreground/95 hover:bg-alert hover:text-alert-foreground"
          size="sm"
          onClick={(e) => {
            if (isSubComment) {
              setSubDocument([]);
            } else {
              // remove the videos
              setDocument(null);
            }
          }}
        />
      </div>
    </>
  );
}

const CreateComment = connect((s) => ({ community: s.community }))(
  CreateCommentComponent
);
export default CreateComment;
